/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DataOptions } from "vuetify/types";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { FILTER_NAMES } from "@/static/filterNames";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import GetCashDocumentListItem from "@/models/api/finances/GetCashDocumentListResponse";
import { ACCESS_TO_DOCUMENT_TYPES } from "@/static/cashDocumentTypes";
import {
  addCashDocument,
  cancelCashDocument,
  createPreviewCashDocument,
  getCashDocumentItems,
  getCashDocumentsTable,
  pairCashDocumentItem,
  removeCashDocument,
  reprintCashDocument,
  showCashDocument,
  unpairCashDocumentItem,
} from "@/requests/finances/requestsCashDocuments";
import { CashDocumentEdit, CashDocumentItemEdit } from "@/models/api/finances/CashDocumentEdit";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "@/static/dateFormats";
import { DOCUMENT_TYPES } from "@/static/documentTypes";
import { KpContractor } from "@/models/api/Contractor";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { getOpenTemporaryFile } from "@/requests/general";
import { CashDocumentItem } from "@/models/api/finances/CashDocumentItem";

export interface FinancesCashDocumentsStateInterface {
  dataTableData: Array<GetCashDocumentListItem>;
  dataTableOptions: DataOptions;
  countServerSide: number;
  filters: null | Array<CriterionItemRequest>;
  isDialog: boolean;
  cashDocumentEdit: null | CashDocumentEdit;
  exchangeRate: number;
  documentType: ACCESS_TO_DOCUMENT_TYPES;
  pairingCashDocumentId: null | number;
  pairingCashDocumentItemId: null | number;
  cashDocumentItems: CashDocumentItem[];
  recordStampId: number | null;
}

export const FinancesCashDocumentsState: FinancesCashDocumentsStateInterface = {
  dataTableData: [],
  dataTableOptions: new DefaultDataOptions(),
  countServerSide: -1,
  filters: null,
  isDialog: false,
  cashDocumentEdit: null,
  exchangeRate: 0,
  documentType: ACCESS_TO_DOCUMENT_TYPES.KP,
  pairingCashDocumentId: null,
  pairingCashDocumentItemId: null,
  cashDocumentItems: [],
  recordStampId: null,
};

const FinancesCashDocumentsModule = defineModule({
  state: FinancesCashDocumentsState as FinancesCashDocumentsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setDataTableData(state, param: Array<GetCashDocumentListItem>) {
      state.dataTableData = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setCountServerSide(state, param: number) {
      state.countServerSide = param;
    },
    setFilters(state, param: Array<CriterionItemRequest>) {
      state.filters = param;
    },
    setIsDialog(state, param: boolean) {
      state.isDialog = param;
    },
    setDocumentType(state, param: ACCESS_TO_DOCUMENT_TYPES) {
      state.documentType = param;
    },
    setCashDocumentEdit(state, param: null | CashDocumentEdit) {
      state.cashDocumentEdit = param;
    },
    setExchangeRate(state, param: number) {
      state.exchangeRate = param;
    },
    setPairingCashDocumentId(state, param: null | number) {
      state.pairingCashDocumentId = param;
    },
    setPairingCashDocumentItemId(state, param: null | number) {
      state.pairingCashDocumentItemId = param;
    },
    setCashDocumentItems(state, param: CashDocumentItem[]) {
      state.cashDocumentItems = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
  },
  actions: {
    async getCashDocumentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      const response = await getCashDocumentsTable({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: state.filters,
        filterName: FILTER_NAMES.CashReportDocumentsStandardFilter,
      });
      commit.setDataTableData(response.items);
      commit.setCountServerSide(response.totalCount);
    },
    async openAddKp(context) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.setDocumentType(ACCESS_TO_DOCUMENT_TYPES.KP);
      await dispatch.createDefaultDocumentTypeModel();
      commit.setIsDialog(true);
    },
    async openAddKw(context) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.setDocumentType(ACCESS_TO_DOCUMENT_TYPES.KW);
      await dispatch.createDefaultDocumentTypeModel();
      commit.setIsDialog(true);
    },
    async createDefaultDocumentTypeModel(context) {
      const { rootDispatch, commit, state } = _moduleActionContext(context);
      const cashDocumentEdit = new CashDocumentEdit();
      cashDocumentEdit.paymentId = null;
      const cashbox = await rootDispatch.cashboxes.getDefaultUserCashbox();
      cashDocumentEdit.cashboxId = cashbox?.id ?? null;
      cashDocumentEdit.issueDate = dayjs().format(DEFAULT_DATE_FORMAT);
      cashDocumentEdit.description = "";
      cashDocumentEdit.amount = null;
      cashDocumentEdit.documentType =
        state.documentType === ACCESS_TO_DOCUMENT_TYPES.KP
          ? DOCUMENT_TYPES.CashIn
          : state.documentType === ACCESS_TO_DOCUMENT_TYPES.KW
          ? DOCUMENT_TYPES.CashOut
          : null;
      cashDocumentEdit.contractor = new KpContractor();
      cashDocumentEdit.documentItems = [new CashDocumentItemEdit()];
      const exchangeRate = await rootDispatch.exchangeRate.getLastExchangeRate({
        exchangeDate: cashDocumentEdit.issueDate,
        currency: cashbox?.currency ?? "PLN",
        skipAlert: true,
      });
      cashDocumentEdit.exchangeDate = exchangeRate.exchangeDate;
      commit.setExchangeRate(exchangeRate.exchangeRateValue);
      commit.setCashDocumentEdit(cashDocumentEdit);
    },
    async saveDocument(context) {
      const { rootDispatch, commit, state, dispatch } = _moduleActionContext(context);
      await rootDispatch.waitForCompleteAllRequests();
      if (state.cashDocumentEdit && state.cashDocumentEdit.cashboxId != null) {
        const cashDocumentEdit = state.cashDocumentEdit as CashDocumentEdit;
        await addCashDocument(cashDocumentEdit);
        rootDispatch.popup.addSavedOkSnackbar();
        commit.setIsDialog(false);
        // noinspection ES6MissingAwait
        dispatch.getCashDocumentsTable();
      } else rootDispatch.popup.addFormErrorsSnackbar();
    },
    async previewSelectedDocuments(context, cashDocumentIds: number[]) {
      const { rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const tempFileId = await createPreviewCashDocument(cashDocumentIds);
      await getOpenTemporaryFile(tempFileId);
    },
    async removeDocument(context, cashDocumentId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        const removeResult = await removeCashDocument(cashDocumentId, false);

        if (removeResult?.show) {
          const buttons = removeResult.hasPermission
            ? [
                {
                  text: filterCapitalize(i18n.t("cancel")),
                  color: "warning",
                  value: false,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("confirm")),
                  color: "warning",
                  value: true,
                },
              ]
            : [
                {
                  text: filterCapitalize(i18n.t("close")),
                  color: "warning",
                  value: false,
                },
              ];

          const secondConfirm = new ConfirmModel(
            removeResult.message ?? "",
            filterCapitalize(filterCapitalize(i18n.t("warning"))),
            buttons
          );

          const isConfirmed = await rootDispatch.popup.addConfirm(secondConfirm);

          if (isConfirmed) {
            await removeCashDocument(cashDocumentId, true);
            await rootDispatch.popup.addSavedOkSnackbar();
          }
        }
        await rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.getCashDocumentsTable();
      }
    },
    async cancelDocument(context, cashDocumentId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("doYouReallyWantToCancelDocument")))
        )
      ) {
        const cancelResult = await cancelCashDocument(cashDocumentId, false);

        if (cancelResult?.show) {
          const buttons = cancelResult.hasPermission
            ? [
                {
                  text: filterCapitalize(i18n.t("cancel")),
                  color: "warning",
                  value: false,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("confirm")),
                  color: "warning",
                  value: true,
                },
              ]
            : [
                {
                  text: filterCapitalize(i18n.t("close")),
                  color: "warning",
                  value: false,
                },
              ];

          const secondConfirm = new ConfirmModel(
            cancelResult.message ?? "",
            filterCapitalize(filterCapitalize(i18n.t("warning"))),
            buttons
          );

          const isConfirmed = await rootDispatch.popup.addConfirm(secondConfirm);

          if (isConfirmed) {
            await cancelCashDocument(cashDocumentId, true);
            await rootDispatch.popup.addSavedOkSnackbar();
          }
        }

        await rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.getCashDocumentsTable();
      }
    },
    async reprintDocument(context, cashDocumentId: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("doYouReallyWantToReprintDocument")))
        )
      ) {
        const reprintResult = await reprintCashDocument(cashDocumentId, false);

        if (reprintResult?.show) {
          const buttons = reprintResult.hasPermission
            ? [
                {
                  text: filterCapitalize(i18n.t("cancel")),
                  color: "warning",
                  value: false,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("confirm")),
                  color: "warning",
                  value: true,
                },
              ]
            : [
                {
                  text: filterCapitalize(i18n.t("close")),
                  color: "warning",
                  value: false,
                },
              ];

          const secondConfirm = new ConfirmModel(
            reprintResult.message ?? "",
            filterCapitalize(filterCapitalize(i18n.t("warning"))),
            buttons
          );

          const isConfirmed = await rootDispatch.popup.addConfirm(secondConfirm);

          if (isConfirmed) {
            await reprintCashDocument(cashDocumentId, true);
          }
        }

        await rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.getCashDocumentsTable();
        await showCashDocument(cashDocumentId);
      }
    },
    async showDocument(context, cashDocumentId: number) {
      await showCashDocument(cashDocumentId);
    },
    async openPairingDialog(context, cashDocumentId: number) {
      const { commit } = _moduleActionContext(context);
      commit.setCashDocumentItems(await getCashDocumentItems(cashDocumentId));
      commit.setPairingCashDocumentId(cashDocumentId);
    },
    async unpairDocumentItem(context, cashDocumentItemId: number) {
      const { rootDispatch, dispatch, state } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("doYouReallyWantToUnpairDocument")))
        )
      ) {
        await unpairCashDocumentItem(cashDocumentItemId);
        await rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.openPairingDialog(state.pairingCashDocumentId as number);
      }
    },
    async openPairingDocument(context, pairingCashDocumentItemId: number) {
      const { commit } = _moduleActionContext(context);
      commit.setPairingCashDocumentItemId(pairingCashDocumentItemId);
    },
    async pairDocumentItem(context, documentId: string) {
      const { rootDispatch, dispatch, state, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("doYouReallyWantToPairDocument")))
        )
      ) {
        await pairCashDocumentItem(state.pairingCashDocumentItemId as number, documentId);
        await rootDispatch.popup.addSavedOkSnackbar();
        commit.setPairingCashDocumentItemId(null);
        await dispatch.openPairingDialog(state.pairingCashDocumentId as number);
      }
    },
  },
});

export default FinancesCashDocumentsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, FinancesCashDocumentsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, FinancesCashDocumentsModule);
